import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Sunrise, Sun } from "lucide-react";
import LiveSignal from "@/components/LiveSignal";
import { BandImage, TileShape } from "@/sections/ttk/Shared";
import useMediaQuery from "@/hooks/useMediaQuery";

const LOOP = ["SENSE", "UNDERSTAND", "ACT", "LEARN"];

const FinalCopy = () => (
  <div className="text-center">
    <div className="flex flex-wrap items-center justify-center gap-3 font-mono text-[9px] tracking-[0.22em] text-ash">
      {LOOP.map((w, i) => (
        <span key={w} className="flex items-center gap-3">
          <span className={i === 1 ? "text-signal" : ""}>{w}</span>
          {i < 3 && <span className="text-line">→</span>}
        </span>
      ))}
    </div>
    <h2
      data-testid="ttk-final-headline"
      className="mt-8 font-display text-4xl font-light text-white sm:text-5xl lg:text-6xl"
    >
      Better <span className="text-signal">together</span>.
    </h2>
    <div className="mt-12 flex flex-col items-center justify-center gap-6 sm:flex-row">
      <a
        href="https://xsdfem-1j.myshopify.com/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="ttk-final-cta"
        className="bg-white px-9 py-4 font-mono text-[11px] tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white"
      >
        EXPLORE TRIFIT.LIFE
      </a>
      <Link
        to="/science"
        data-testid="ttk-final-secondary"
        className="font-mono text-[10px] tracking-[0.2em] text-ash transition-colors duration-300 hover:text-white"
      >
        DISCOVER THE SCIENCE →
      </Link>
    </div>
  </div>
);

const KitComposition = () => (
  <div className="flex items-center justify-center gap-6 md:gap-10">
    <BandImage alt="TriKarma Band" fade className="w-40 md:w-56" />
    <div
      data-testid="ttk-final-twin"
      className="relative overflow-hidden rounded-[2.1rem] border border-signal/40 bg-[#0A0408] w-28 md:w-36"
    >
      <img
        src="/assets/app/app_home_twin.webp"
        alt="Trifit.life · THE TWIN · live 3D body model"
        className="block h-auto w-full"
      />
    </div>
    <div className="flex flex-col gap-3">
      <TileShape label="MORNING" icon={Sunrise} className="h-16 w-16 md:h-20 md:w-20" />
      <TileShape label="SUNLIGHT" icon={Sun} className="ml-4 h-12 w-12 opacity-80" />
    </div>
  </div>
);

const TtkTogetherDesktop = () => {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const sigX = useTransform(p, [0.05, 0.4], ["-32vw", "0vw"]);
  const tileX = useTransform(p, [0.05, 0.4], ["32vw", "0vw"]);
  const fragOp = useTransform(p, [0.05, 0.15, 0.5, 0.62], [0, 1, 1, 0]);
  const insight = useTransform(p, [0.34, 0.44, 0.5, 0.62], [0, 1, 1, 0]);
  const finalOp = useTransform(p, [0.62, 0.74], [0, 1]);
  const finalY = useTransform(p, [0.62, 0.74], [40, 0]);

  return (
    <section ref={ref} data-testid="ttk-together-section" className="relative h-[260vh]">
      <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden">
        <p className="absolute left-12 top-24 font-mono text-[11px] tracking-[0.28em] text-ash">
          06 · BETTER TOGETHER
        </p>

        {/* converging fragments */}
        <motion.div
          style={{ x: sigX, opacity: fragOp }}
          className="absolute left-[14%] top-[38%] w-56 rounded-lg border border-line bg-[#0C060A] p-4"
        >
          <p className="font-mono text-[8px] tracking-[0.18em] text-ash">BAND · SIGNAL</p>
          <LiveSignal className="mt-2 h-5 w-full" color="#D2378B" />
          <p className="mt-2 font-mono text-[8px] tracking-[0.12em] text-fog">RECOVERY · LOW</p>
        </motion.div>

        <motion.div
          style={{ x: tileX, opacity: fragOp }}
          className="absolute right-[14%] top-[52%] w-56 rounded-lg border border-line bg-[#0C060A] p-4"
        >
          <p className="font-mono text-[8px] tracking-[0.18em] text-ash">TILE · LOGGED</p>
          <p className="mt-2 font-mono text-[9px] tracking-[0.12em] text-white">
            HYDRATION · 14:32 <span className="text-signal">✓</span>
          </p>
        </motion.div>

        <motion.div
          style={{ opacity: insight }}
          className="absolute left-1/2 top-[44%] w-64 -translate-x-1/2 rounded-lg border border-white/25 bg-[#0C060A] p-4 text-center"
        >
          <p className="font-mono text-[8px] tracking-[0.18em] text-ash">TRIFIT · NEXT ACTION</p>
          <p className="mt-2 font-mono text-[9px] leading-relaxed tracking-[0.1em] text-white">
            EASY SESSION AT 18:00
          </p>
        </motion.div>

        {/* final resolved composition */}
        <motion.div style={{ opacity: finalOp, y: finalY }} className="px-6 text-center">
          <KitComposition />
          <div className="mt-14">
            <FinalCopy />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const TtkTogetherMobile = () => (
  <section data-testid="ttk-together-section-mobile" className="relative px-6 py-24">
    <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
      06 · BETTER TOGETHER
    </p>
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8 }}
      className="mt-12"
    >
      <KitComposition />
      <div className="mt-14">
        <FinalCopy />
      </div>
    </motion.div>
  </section>
);

const TtkTogether = () => {
  const pinned = useMediaQuery("(min-width: 1024px) and (min-height: 620px)");
  return pinned ? <TtkTogetherDesktop /> : <TtkTogetherMobile />;
};

export default TtkTogether;
